// src/components/Reports/StandardReports.js
import React, { useState, useCallback } from "react";
import { localYmd } from '../../utils/dateOnly';
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Chart } from "primereact/chart";
import { Tag } from "primereact/tag";
import { Divider } from "primereact/divider";
import { InputNumber } from "primereact/inputnumber";
import { ProgressBar } from "primereact/progressbar";
import { Message } from "primereact/message";
import axiosInstance from "../../services/axiosInstance";
import { handleApiError } from "../apiErrorHandler";

// ─── Helpers ───────────────────────────────────────────────────────────────

const fmt = (val) =>
    Number(val || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtInt = (val) => Number(val || 0).toLocaleString("en-US");

const toISO = (d) => (d instanceof Date ? localYmd(d) : d);

const CHART_COLORS = [
    "#3B82F6", "#10B981", "#F59E0B", "#EF4444",
    "#8B5CF6", "#06B6D4", "#F97316", "#EC4899",
];
const chartColor = (i) => CHART_COLORS[i % CHART_COLORS.length];

const BASE_CHART_OPTIONS = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: "top" } },
};

const LINE_OPTIONS = {
    ...BASE_CHART_OPTIONS,
    interaction: { mode: "index", intersect: false },
    scales: { y: { beginAtZero: true } },
};

const BAR_OPTIONS = {
    ...BASE_CHART_OPTIONS,
    scales: { y: { beginAtZero: true } },
};

const CURRENCY_OPTIONS = {
    ...BAR_OPTIONS,
    scales: { y: { beginAtZero: true, ticks: { callback: (v) => `Rs. ${v.toLocaleString()}` } } },
};

const CURRENCY_LINE_OPTIONS = {
    ...LINE_OPTIONS,
    scales: { y: { beginAtZero: true, ticks: { callback: (v) => `Rs. ${v.toLocaleString()}` } } },
};

// ─── Report catalogue ──────────────────────────────────────────────────────

const REPORT_GROUPS = [
    {
        label: "Sales & Revenue",
        icon: "pi pi-dollar",
        color: "#3B82F6",
        reports: [
            {
                id: "sales-summary",
                label: "Daily Sales Summary",
                endpoint: "/api/reports/v2/sales-summary/",
                description: "Revenue, invoice counts, and discounts by day.",
                hasDateRange: true,
                defaultDays: 30,
                chartType: "line",
                chartOptions: CURRENCY_LINE_OPTIONS,
                chartDataFn: (data) => ({
                    labels: data.map((r) => r.date),
                    datasets: [
                        {
                            label: "Net Revenue",
                            data: data.map((r) => parseFloat(r.net_revenue)),
                            borderColor: "#3B82F6",
                            backgroundColor: "rgba(59,130,246,0.15)",
                            fill: true,
                            tension: 0.3,
                            pointRadius: 3,
                        },
                        {
                            label: "Gross Revenue",
                            data: data.map((r) => parseFloat(r.gross_revenue)),
                            borderColor: "#10B981",
                            backgroundColor: "rgba(16,185,129,0)",
                            fill: false,
                            tension: 0.3,
                            pointRadius: 3,
                        },
                    ],
                }),
                columns: [
                    { field: "date", header: "Date", sortable: true },
                    { field: "invoice_count", header: "Invoices", sortable: true, body: (r) => fmtInt(r.invoice_count) },
                    { field: "gross_revenue", header: "Gross Revenue", sortable: true, body: (r) => `Rs. ${fmt(r.gross_revenue)}` },
                    { field: "discount_total", header: "Discounts", sortable: true, body: (r) => `Rs. ${fmt(r.discount_total)}` },
                    { field: "net_revenue", header: "Net Revenue", sortable: true, body: (r) => `Rs. ${fmt(r.net_revenue)}` },
                ],
            },
            {
                id: "revenue-by-product",
                label: "Revenue by Product",
                endpoint: "/api/reports/v2/revenue-by-product/",
                description: "Top products by revenue within the selected period.",
                hasDateRange: true,
                defaultDays: 30,
                extraParams: { limit: 20 },
                extraControls: (params, setParams) => (
                    <div className="flex align-items-center gap-2">
                        <label className="text-sm text-600">Top</label>
                        <InputNumber value={params.limit || 20} onValueChange={(e) => setParams((p) => ({ ...p, limit: e.value }))} min={5} max={100} style={{ width: 80 }} />
                        <label className="text-sm text-600">products</label>
                    </div>
                ),
                chartType: "bar",
                chartOptions: CURRENCY_OPTIONS,
                chartDataFn: (data) => ({
                    labels: data.slice(0, 15).map((r) => r.product_name.length > 20 ? r.product_name.slice(0, 20) + "…" : r.product_name),
                    datasets: [{
                        label: "Revenue (Rs.)",
                        data: data.slice(0, 15).map((r) => parseFloat(r.total_revenue)),
                        backgroundColor: data.slice(0, 15).map((_, i) => chartColor(i) + "CC"),
                        borderColor: data.slice(0, 15).map((_, i) => chartColor(i)),
                        borderWidth: 1,
                    }],
                }),
                columns: [
                    { field: "product_code", header: "Code", sortable: true },
                    { field: "product_name", header: "Product", sortable: true },
                    { field: "category", header: "Category", sortable: true },
                    { field: "total_qty", header: "Qty Sold", sortable: true, body: (r) => fmtInt(r.total_qty) },
                    { field: "total_revenue", header: "Revenue", sortable: true, body: (r) => `Rs. ${fmt(r.total_revenue)}` },
                ],
            },
            {
                id: "revenue-by-category",
                label: "Revenue by Category",
                endpoint: "/api/reports/v2/revenue-by-category/",
                description: "Sales breakdown by product category.",
                hasDateRange: true,
                defaultDays: 30,
                chartType: "doughnut",
                chartOptions: BASE_CHART_OPTIONS,
                chartDataFn: (data) => ({
                    labels: data.map((r) => r.category),
                    datasets: [{
                        data: data.map((r) => parseFloat(r.total_revenue)),
                        backgroundColor: data.map((_, i) => chartColor(i) + "CC"),
                        borderColor: data.map((_, i) => chartColor(i)),
                        borderWidth: 2,
                    }],
                }),
                columns: [
                    { field: "category", header: "Category", sortable: true },
                    { field: "product_count", header: "Products", sortable: true },
                    { field: "total_qty", header: "Qty Sold", sortable: true, body: (r) => fmtInt(r.total_qty) },
                    { field: "total_revenue", header: "Revenue", sortable: true, body: (r) => `Rs. ${fmt(r.total_revenue)}` },
                ],
            },
            {
                id: "invoice-status-breakdown",
                label: "Invoice Status Breakdown",
                endpoint: "/api/reports/v2/invoice-status-breakdown/",
                description: "Count and value of invoices by status.",
                hasDateRange: true,
                defaultDays: 30,
                chartType: "bar",
                chartOptions: { ...BAR_OPTIONS, indexAxis: "y" },
                chartDataFn: (data) => ({
                    labels: data.map((r) => r.status),
                    datasets: [{
                        label: "Invoice Count",
                        data: data.map((r) => r.count),
                        backgroundColor: data.map((_, i) => chartColor(i) + "CC"),
                        borderColor: data.map((_, i) => chartColor(i)),
                        borderWidth: 1,
                    }],
                }),
                columns: [
                    {
                        field: "status", header: "Status", sortable: true,
                        body: (r) => {
                            const map = { Complete: "success", Cancelled: "danger", Open: "info", "Payment Pending": "warning" };
                            return <Tag value={r.status} severity={map[r.status] || "secondary"} />;
                        },
                    },
                    { field: "count", header: "Count", sortable: true, body: (r) => fmtInt(r.count) },
                    { field: "total_value", header: "Total Value", sortable: true, body: (r) => `Rs. ${fmt(r.total_value)}` },
                ],
            },
            {
                id: "payment-collection",
                label: "Payment Collection",
                endpoint: "/api/reports/v2/payment-collection/",
                description: "Daily payment totals by method (cash/card).",
                hasDateRange: true,
                defaultDays: 30,
                chartType: "bar",
                chartOptions: CURRENCY_OPTIONS,
                chartDataFn: (data) => {
                    const dates = [...new Set(data.map((r) => r.date))].sort();
                    const methods = [...new Set(data.map((r) => r.method))];
                    return {
                        labels: dates,
                        datasets: methods.map((method, i) => ({
                            label: method,
                            data: dates.map((d) => {
                                const found = data.find((r) => r.date === d && r.method === method);
                                return found ? parseFloat(found.total) : 0;
                            }),
                            backgroundColor: chartColor(i) + "CC",
                            borderColor: chartColor(i),
                            borderWidth: 1,
                        })),
                    };
                },
                columns: [
                    { field: "date", header: "Date", sortable: true },
                    { field: "method", header: "Method", sortable: true, body: (r) => <Tag value={r.method} severity={r.method === "cash" ? "success" : "info"} /> },
                    { field: "count", header: "Transactions", sortable: true, body: (r) => fmtInt(r.count) },
                    { field: "total", header: "Total", sortable: true, body: (r) => `Rs. ${fmt(r.total)}` },
                ],
            },
            {
                id: "sales-month-on-month",
                label: "Month-on-Month Sales",
                endpoint: "/api/reports/v2/sales-month-on-month/",
                description: "Monthly revenue trend with MoM change vs prior month.",
                hasDateRange: false,
                extraParams: { months: 13 },
                extraControls: (params, setParams) => (
                    <div className="flex align-items-center gap-2">
                        <label className="text-sm text-600">Last</label>
                        <InputNumber value={params.months || 13} onValueChange={(e) => setParams((p) => ({ ...p, months: e.value }))} min={3} max={36} style={{ width: 80 }} />
                        <label className="text-sm text-600">months</label>
                    </div>
                ),
                chartType: "line",
                chartOptions: CURRENCY_LINE_OPTIONS,
                chartDataFn: (data) => ({
                    labels: data.map((r) => r.month_label),
                    datasets: [
                        {
                            label: "Net Revenue",
                            data: data.map((r) => parseFloat(r.net_revenue)),
                            borderColor: "#3B82F6",
                            backgroundColor: "rgba(59,130,246,0.15)",
                            fill: true,
                            tension: 0.3,
                            pointRadius: 4,
                            pointBackgroundColor: data.map((r) =>
                                r.mom_change === null ? "#3B82F6"
                                : parseFloat(r.mom_change) >= 0 ? "#10B981" : "#EF4444"
                            ),
                            pointBorderColor: data.map((r) =>
                                r.mom_change === null ? "#3B82F6"
                                : parseFloat(r.mom_change) >= 0 ? "#10B981" : "#EF4444"
                            ),
                            pointRadius: 6,
                        },
                    ],
                }),
                summaryFn: (meta) => {
                    const data = meta?.data;
                    if (!data || data.length < 2) return null;
                    const last = data[data.length - 1];
                    const prev = data[data.length - 2];
                    const change = parseFloat(last.mom_change || 0);
                    const pct = last.mom_pct;
                    return (
                        <div className="flex gap-3 mb-3 flex-wrap">
                            {[
                                { label: "Latest Month", value: last.month_label, color: "#6366F1" },
                                { label: "Net Revenue", value: `Rs. ${fmt(last.net_revenue)}`, color: "#3B82F6" },
                                { label: "Prior Month", value: `Rs. ${fmt(prev.net_revenue)}`, color: "#64748B" },
                                { label: "MoM Change", value: `${change >= 0 ? "+" : ""}Rs. ${fmt(Math.abs(change))}`, color: change >= 0 ? "#10B981" : "#EF4444" },
                                { label: "MoM %", value: pct !== null ? `${pct >= 0 ? "+" : ""}${pct}%` : "—", color: pct !== null && pct >= 0 ? "#10B981" : "#EF4444" },
                            ].map((kpi) => (
                                <Card key={kpi.label} className="shadow-1 border-round-lg flex-1" style={{ minWidth: 110, borderTop: `3px solid ${kpi.color}` }}>
                                    <div className="text-xs text-600 mb-1">{kpi.label}</div>
                                    <div className="font-bold text-lg" style={{ color: kpi.color }}>{kpi.value}</div>
                                </Card>
                            ))}
                        </div>
                    );
                },
                columns: [
                    { field: "month_label", header: "Month", sortable: true },
                    { field: "invoice_count", header: "Invoices", sortable: true, body: (r) => fmtInt(r.invoice_count) },
                    { field: "gross_revenue", header: "Gross Revenue", sortable: true, body: (r) => `Rs. ${fmt(r.gross_revenue)}` },
                    { field: "discount_total", header: "Discounts", sortable: true, body: (r) => `Rs. ${fmt(r.discount_total)}` },
                    { field: "net_revenue", header: "Net Revenue", sortable: true, body: (r) => `Rs. ${fmt(r.net_revenue)}` },
                    {
                        field: "mom_change", header: "MoM Change", sortable: true,
                        body: (r) => {
                            if (r.mom_change === null) return <span className="text-400">—</span>;
                            const v = parseFloat(r.mom_change);
                            return <span style={{ color: v >= 0 ? "#10B981" : "#EF4444", fontWeight: 600 }}>{v >= 0 ? "▲" : "▼"} Rs. {fmt(Math.abs(v))}</span>;
                        },
                    },
                    {
                        field: "mom_pct", header: "MoM %", sortable: true,
                        body: (r) => {
                            if (r.mom_pct === null) return <span className="text-400">—</span>;
                            const v = r.mom_pct;
                            return <span style={{ color: v >= 0 ? "#10B981" : "#EF4444", fontWeight: 600 }}>{v >= 0 ? "+" : ""}{v}%</span>;
                        },
                    },
                ],
            },
            {
                id: "sales-year-on-year",
                label: "Year-on-Year Sales",
                endpoint: "/api/reports/v2/sales-year-on-year/",
                description: "Monthly revenue comparison across multiple years.",
                hasDateRange: false,
                extraParams: { years: 3 },
                extraControls: (params, setParams) => (
                    <div className="flex align-items-center gap-2">
                        <label className="text-sm text-600">Compare last</label>
                        <InputNumber value={params.years || 3} onValueChange={(e) => setParams((p) => ({ ...p, years: e.value }))} min={2} max={5} style={{ width: 70 }} />
                        <label className="text-sm text-600">years</label>
                    </div>
                ),
                chartType: "line",
                chartOptions: CURRENCY_LINE_OPTIONS,
                chartDataFn: (_, meta) => {
                    if (!meta?.monthly) return null;
                    const years = meta.years || [];
                    const YOY_COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6"];
                    return {
                        labels: meta.monthly.map((r) => r.month_label),
                        datasets: years.map((y, i) => ({
                            label: String(y),
                            data: meta.monthly.map((r) => parseFloat(r[String(y)] || 0)),
                            borderColor: YOY_COLORS[i],
                            backgroundColor: YOY_COLORS[i] + "20",
                            fill: false,
                            tension: 0.3,
                            pointRadius: 4,
                            borderWidth: 2,
                        })),
                    };
                },
                summaryFn: (meta) => {
                    const totals = meta?.yearly_totals;
                    if (!totals?.length) return null;
                    const YOY_COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6"];
                    return (
                        <div className="flex gap-3 mb-3 flex-wrap">
                            {totals.map((t, i) => (
                                <Card key={t.year} className="shadow-1 border-round-lg flex-1" style={{ minWidth: 130, borderTop: `3px solid ${YOY_COLORS[i]}` }}>
                                    <div className="text-xs text-600 mb-1">{t.year}</div>
                                    <div className="font-bold text-lg" style={{ color: YOY_COLORS[i] }}>Rs. {fmt(t.net_revenue)}</div>
                                    <div className="text-xs text-500 mt-1">{fmtInt(t.invoice_count)} invoices</div>
                                </Card>
                            ))}
                        </div>
                    );
                },
                tableDataFn: (_, meta) => meta?.yearly_totals || [],
                columns: [
                    { field: "year", header: "Year", sortable: true },
                    { field: "invoice_count", header: "Invoices", sortable: true, body: (r) => fmtInt(r.invoice_count) },
                    { field: "gross_revenue", header: "Gross Revenue", sortable: true, body: (r) => `Rs. ${fmt(r.gross_revenue)}` },
                    { field: "discount_total", header: "Discounts", sortable: true, body: (r) => `Rs. ${fmt(r.discount_total)}` },
                    { field: "net_revenue", header: "Net Revenue", sortable: true, body: (r) => `Rs. ${fmt(r.net_revenue)}` },
                ],
            },
        ],
    },
    {
        label: "Inventory",
        icon: "pi pi-box",
        color: "#10B981",
        reports: [
            {
                id: "stock-levels",
                label: "Stock Levels",
                endpoint: "/api/reports/v2/stock-levels/",
                description: "Current stock across all active batches. Red = below reorder level.",
                hasDateRange: false,
                extraParams: { below_reorder: "" },
                extraControls: (params, setParams) => (
                    <div className="flex align-items-center gap-2">
                        <label className="text-sm text-600">
                            <input type="checkbox" checked={params.below_reorder === "1"} onChange={(e) => setParams((p) => ({ ...p, below_reorder: e.target.checked ? "1" : "" }))} className="mr-2" />
                            Low stock only
                        </label>
                    </div>
                ),
                chartType: "bar",
                chartOptions: BAR_OPTIONS,
                chartDataFn: (data) => {
                    const top20 = [...data].sort((a, b) => a.total_qty - b.total_qty).slice(0, 20);
                    return {
                        labels: top20.map((r) => r.product_name.length > 18 ? r.product_name.slice(0, 18) + "…" : r.product_name),
                        datasets: [{
                            label: "Stock Qty",
                            data: top20.map((r) => r.total_qty),
                            backgroundColor: top20.map((r) => r.below_reorder ? "rgba(239,68,68,0.7)" : "rgba(16,185,129,0.7)"),
                            borderColor: top20.map((r) => r.below_reorder ? "#EF4444" : "#10B981"),
                            borderWidth: 1,
                        }],
                    };
                },
                columns: [
                    { field: "product_code", header: "Code", sortable: true },
                    { field: "product_name", header: "Product", sortable: true },
                    { field: "category", header: "Category", sortable: true },
                    { field: "total_qty", header: "Total Qty", sortable: true, body: (r) => fmtInt(r.total_qty) },
                    { field: "reorder_level", header: "Reorder Level", sortable: true, body: (r) => fmtInt(r.reorder_level) },
                    {
                        field: "below_reorder", header: "Status", sortable: true,
                        body: (r) => <Tag value={r.below_reorder ? "Low Stock" : "OK"} severity={r.below_reorder ? "danger" : "success"} />,
                    },
                ],
            },
            {
                id: "expiry-tracking",
                label: "Expiry Tracking",
                endpoint: "/api/reports/v2/expiry-tracking/",
                description: "Batches expiring within selected window.",
                hasDateRange: false,
                extraParams: { days: 90 },
                extraControls: (params, setParams) => (
                    <div className="flex align-items-center gap-2">
                        <label className="text-sm text-600">Within</label>
                        <InputNumber value={params.days || 90} onValueChange={(e) => setParams((p) => ({ ...p, days: e.value }))} min={1} max={365} style={{ width: 90 }} />
                        <label className="text-sm text-600">days</label>
                    </div>
                ),
                chartType: "bar",
                chartOptions: BAR_OPTIONS,
                chartDataFn: (data) => {
                    const buckets = { "Expired": 0, "0–30 days": 0, "31–60 days": 0, "61–90 days": 0, "90+ days": 0 };
                    data.forEach((r) => {
                        const d = r.days_to_expiry;
                        if (d < 0) buckets["Expired"] += r.quantity;
                        else if (d <= 30) buckets["0–30 days"] += r.quantity;
                        else if (d <= 60) buckets["31–60 days"] += r.quantity;
                        else if (d <= 90) buckets["61–90 days"] += r.quantity;
                        else buckets["90+ days"] += r.quantity;
                    });
                    return {
                        labels: Object.keys(buckets),
                        datasets: [{
                            label: "Units",
                            data: Object.values(buckets),
                            backgroundColor: ["#EF4444CC", "#F97316CC", "#F59E0BCC", "#10B981CC", "#3B82F6CC"],
                            borderWidth: 1,
                        }],
                    };
                },
                columns: [
                    { field: "product_code", header: "Code", sortable: true },
                    { field: "product_name", header: "Product", sortable: true },
                    { field: "batch_number", header: "Batch", sortable: true },
                    { field: "quantity", header: "Qty", sortable: true, body: (r) => fmtInt(r.quantity) },
                    { field: "expiry_date", header: "Expiry Date", sortable: true },
                    {
                        field: "days_to_expiry", header: "Days Left", sortable: true,
                        body: (r) => (
                            <span style={{ color: r.expired ? "#EF4444" : r.days_to_expiry <= 30 ? "#F97316" : "#10B981", fontWeight: 600 }}>
                                {r.expired ? `Expired (${r.days_to_expiry}d)` : `${r.days_to_expiry}d`}
                            </span>
                        ),
                    },
                    { field: "supplier", header: "Supplier", sortable: true },
                    { field: "department", header: "Department", sortable: true },
                ],
            },
            {
                id: "slow-moving-stock",
                label: "Slow-Moving Stock",
                endpoint: "/api/reports/v2/slow-moving-stock/",
                description: "Products with stock but zero sales in the selected window.",
                hasDateRange: false,
                extraParams: { days: 60 },
                extraControls: (params, setParams) => (
                    <div className="flex align-items-center gap-2">
                        <label className="text-sm text-600">No sales in last</label>
                        <InputNumber value={params.days || 60} onValueChange={(e) => setParams((p) => ({ ...p, days: e.value }))} min={7} max={365} style={{ width: 90 }} />
                        <label className="text-sm text-600">days</label>
                    </div>
                ),
                chartType: "bar",
                chartOptions: BAR_OPTIONS,
                chartDataFn: (data) => ({
                    labels: data.slice(0, 15).map((r) => r.product_name.length > 18 ? r.product_name.slice(0, 18) + "…" : r.product_name),
                    datasets: [{
                        label: "Stock Qty",
                        data: data.slice(0, 15).map((r) => r.total_qty),
                        backgroundColor: "rgba(239,68,68,0.6)",
                        borderColor: "#EF4444",
                        borderWidth: 1,
                    }],
                }),
                columns: [
                    { field: "product_code", header: "Code", sortable: true },
                    { field: "product_name", header: "Product", sortable: true },
                    { field: "category", header: "Category", sortable: true },
                    { field: "total_qty", header: "Stock Qty", sortable: true, body: (r) => fmtInt(r.total_qty) },
                    { field: "last_purchase", header: "Last Purchase", sortable: true },
                ],
            },
            {
                id: "stock-movement",
                label: "Stock Movement",
                endpoint: "/api/reports/v2/stock-movement/",
                description: "Stock in (GRN) vs out (invoiced) by day.",
                hasDateRange: true,
                defaultDays: 30,
                chartType: "bar",
                chartOptions: BAR_OPTIONS,
                chartDataFn: (data) => {
                    const dates = [...new Set(data.map((r) => r.date))].sort();
                    return {
                        labels: dates,
                        datasets: [
                            {
                                label: "IN (Received)",
                                data: dates.map((d) => data.filter((r) => r.date === d && r.type === "IN").reduce((s, r) => s + r.qty, 0)),
                                backgroundColor: "rgba(16,185,129,0.7)",
                                borderColor: "#10B981",
                                borderWidth: 1,
                            },
                            {
                                label: "OUT (Invoiced)",
                                data: dates.map((d) => data.filter((r) => r.date === d && r.type === "OUT").reduce((s, r) => s + r.qty, 0)),
                                backgroundColor: "rgba(239,68,68,0.7)",
                                borderColor: "#EF4444",
                                borderWidth: 1,
                            },
                        ],
                    };
                },
                columns: [
                    { field: "date", header: "Date", sortable: true },
                    { field: "product_name", header: "Product", sortable: true },
                    { field: "type", header: "Type", sortable: true, body: (r) => <Tag value={r.type} severity={r.type === "IN" ? "success" : "danger"} /> },
                    { field: "qty", header: "Qty", sortable: true, body: (r) => fmtInt(r.qty) },
                ],
            },
        ],
    },
    {
        label: "Finance",
        icon: "pi pi-wallet",
        color: "#8B5CF6",
        reports: [
            {
                id: "supplier-payables-aging",
                label: "Supplier Payables Aging",
                endpoint: "/api/reports/v2/supplier-payables-aging/",
                description: "Outstanding supplier invoices bucketed by aging.",
                hasDateRange: false,
                chartType: "bar",
                chartOptions: CURRENCY_OPTIONS,
                chartDataFn: (_, meta) => {
                    if (!meta?.summary) return null;
                    const s = meta.summary;
                    return {
                        labels: ["Current", "1–30 days", "31–60 days", "61–90 days", "90+ days"],
                        datasets: [{
                            label: "Outstanding (Rs.)",
                            data: [s.current, s["1_30"], s["31_60"], s["61_90"], s["90_plus"]],
                            backgroundColor: ["#3B82F6CC", "#10B981CC", "#F59E0BCC", "#F97316CC", "#EF4444CC"],
                            borderWidth: 1,
                        }],
                    };
                },
                columns: [
                    { field: "supplier", header: "Supplier", sortable: true },
                    { field: "invoice_number", header: "Invoice No.", sortable: true },
                    { field: "total_amount", header: "Total", sortable: true, body: (r) => `Rs. ${fmt(r.total_amount)}` },
                    { field: "outstanding_amount", header: "Outstanding", sortable: true, body: (r) => `Rs. ${fmt(r.outstanding_amount)}` },
                    { field: "due_date", header: "Due Date", sortable: true },
                    {
                        field: "status", header: "Status", sortable: true,
                        body: (r) => {
                            const map = { open: "danger", partially_paid: "warning", closed: "success" };
                            return <Tag value={r.status} severity={map[r.status] || "secondary"} />;
                        },
                    },
                    {
                        field: "aging_bucket", header: "Aging", sortable: true,
                        body: (r) => {
                            const map = { current: "success", "1_30": "info", "31_60": "warning", "61_90": "warning", "90_plus": "danger" };
                            const labels = { current: "Current", "1_30": "1-30d", "31_60": "31-60d", "61_90": "61-90d", "90_plus": "90+d" };
                            return <Tag value={labels[r.aging_bucket] || r.aging_bucket} severity={map[r.aging_bucket] || "secondary"} />;
                        },
                    },
                ],
            },
        ],
    },
    {
        label: "Customers",
        icon: "pi pi-users",
        color: "#F59E0B",
        reports: [
            {
                id: "customer-purchase-frequency",
                label: "Customer Purchase Frequency",
                endpoint: "/api/reports/v2/customer-purchase-frequency/",
                description: "Invoice count and total spend per customer.",
                hasDateRange: true,
                defaultDays: 90,
                extraParams: { min_invoices: 1 },
                extraControls: (params, setParams) => (
                    <div className="flex align-items-center gap-2">
                        <label className="text-sm text-600">Min invoices</label>
                        <InputNumber value={params.min_invoices || 1} onValueChange={(e) => setParams((p) => ({ ...p, min_invoices: e.value }))} min={1} max={100} style={{ width: 80 }} />
                    </div>
                ),
                chartType: "bar",
                chartOptions: CURRENCY_OPTIONS,
                chartDataFn: (data) => ({
                    labels: data.slice(0, 15).map((r) => r.customer_name || "Unknown"),
                    datasets: [{
                        label: "Total Spend (Rs.)",
                        data: data.slice(0, 15).map((r) => parseFloat(r.total_spend)),
                        backgroundColor: "rgba(245,158,11,0.7)",
                        borderColor: "#F59E0B",
                        borderWidth: 1,
                    }],
                }),
                columns: [
                    { field: "customer_name", header: "Customer", sortable: true },
                    { field: "phone", header: "Phone", sortable: true },
                    { field: "category", header: "Category", sortable: true },
                    { field: "invoice_count", header: "Invoices", sortable: true, body: (r) => fmtInt(r.invoice_count) },
                    { field: "total_spend", header: "Total Spend", sortable: true, body: (r) => `Rs. ${fmt(r.total_spend)}` },
                    { field: "last_purchase", header: "Last Purchase", sortable: true },
                ],
            },
        ],
    },
    {
        label: "Prescriptions",
        icon: "pi pi-file",
        color: "#06B6D4",
        reports: [
            {
                id: "refill-compliance",
                label: "Refill Compliance",
                endpoint: "/api/reports/v2/refill-compliance/",
                description: "Completed vs missed refill reminders with per-customer detail.",
                hasDateRange: true,
                defaultDays: 90,
                chartType: "doughnut",
                chartOptions: BASE_CHART_OPTIONS,
                chartDataFn: (_, meta) => {
                    if (!meta?.summary) return null;
                    const s = meta.summary;
                    return {
                        labels: ["Completed", "Missed", "Pending"],
                        datasets: [{
                            data: [s.completed, s.missed, s.pending],
                            backgroundColor: ["rgba(16,185,129,0.8)", "rgba(239,68,68,0.8)", "rgba(245,158,11,0.8)"],
                            borderColor: ["#10B981", "#EF4444", "#F59E0B"],
                            borderWidth: 2,
                        }],
                    };
                },
                summaryFn: (meta) => {
                    if (!meta?.summary) return null;
                    const s = meta.summary;
                    return (
                        <div className="flex gap-3 mb-3 flex-wrap">
                            {[
                                { label: "Total", value: s.total, color: "#6366F1" },
                                { label: "Completed", value: s.completed, color: "#10B981" },
                                { label: "Missed", value: s.missed, color: "#EF4444" },
                                { label: "Pending", value: s.pending, color: "#F59E0B" },
                                { label: "Compliance Rate", value: `${s.compliance_rate_pct}%`, color: "#3B82F6" },
                            ].map((kpi) => (
                                <Card key={kpi.label} className="shadow-1 border-round-lg flex-1" style={{ minWidth: 100, borderTop: `3px solid ${kpi.color}` }}>
                                    <div className="text-xs text-600 mb-1">{kpi.label}</div>
                                    <div className="text-xl font-bold" style={{ color: kpi.color }}>{kpi.value}</div>
                                </Card>
                            ))}
                        </div>
                    );
                },
                columns: [
                    { field: "customer_name", header: "Customer", sortable: true },
                    { field: "phone", header: "Phone", sortable: true },
                    { field: "total", header: "Total", sortable: true },
                    { field: "completed", header: "Completed", sortable: true },
                    {
                        field: "missed", header: "Missed", sortable: true,
                        body: (r) => <span style={{ color: r.missed > 0 ? "#EF4444" : undefined, fontWeight: r.missed > 0 ? 600 : undefined }}>{r.missed}</span>,
                    },
                    { field: "pending", header: "Pending", sortable: true },
                ],
            },
            {
                id: "prescription-trends",
                label: "Prescription Trends",
                endpoint: "/api/reports/v2/prescription-trends/",
                description: "Monthly prescriptions and refill reminders created.",
                hasDateRange: true,
                defaultDays: 365,
                chartType: "line",
                chartOptions: LINE_OPTIONS,
                chartDataFn: (data) => ({
                    labels: data.map((r) => r.month),
                    datasets: [
                        {
                            label: "Prescriptions",
                            data: data.map((r) => r.prescriptions_created),
                            borderColor: "#06B6D4",
                            backgroundColor: "rgba(6,182,212,0.15)",
                            fill: true,
                            tension: 0.3,
                            pointRadius: 3,
                        },
                        {
                            label: "Refill Reminders",
                            data: data.map((r) => r.refill_reminders_created),
                            borderColor: "#8B5CF6",
                            backgroundColor: "rgba(139,92,246,0.1)",
                            fill: true,
                            tension: 0.3,
                            pointRadius: 3,
                        },
                    ],
                }),
                columns: [
                    { field: "month", header: "Month", sortable: true },
                    { field: "prescriptions_created", header: "Prescriptions Created", sortable: true },
                    { field: "refill_reminders_created", header: "Refill Reminders Created", sortable: true },
                ],
            },
        ],
    },
];

// ─── ReportViewer ─────────────────────────────────────────────────────────

const ReportViewer = ({ report }) => {
    const today = new Date();
    const defaultFrom = new Date(today);
    defaultFrom.setDate(today.getDate() - ((report.defaultDays || 30) - 1));

    const [dateRange, setDateRange] = useState([defaultFrom, today]);
    const [extraParams, setExtraParams] = useState(report.extraParams || {});
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const buildUrl = useCallback(() => {
        const params = new URLSearchParams();
        if (report.hasDateRange && dateRange[0] && dateRange[1]) {
            params.set("from_date", toISO(dateRange[0]));
            params.set("to_date", toISO(dateRange[1]));
        }
        Object.entries(extraParams).forEach(([k, v]) => {
            if (v !== "" && v !== null && v !== undefined) params.set(k, v);
        });
        const qs = params.toString();
        return qs ? `${report.endpoint}?${qs}` : report.endpoint;
    }, [report, dateRange, extraParams]);

    const runReport = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get(buildUrl());
            setResult(res.data);
        } catch (err) {
            setError("Failed to load report. Please try again.");
            handleApiError(err);
        } finally {
            setLoading(false);
        }
    };

    const exportCsv = async () => {
        try {
            const url = buildUrl();
            const exportUrl = url + (url.includes("?") ? "&" : "?") + "export=csv";
            const res = await axiosInstance.get(exportUrl, { responseType: "blob" });
            const blob = new Blob([res.data], { type: "text/csv" });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = `${report.id}.csv`;
            link.click();
            URL.revokeObjectURL(link.href);
        } catch (err) {
            handleApiError(err);
        }
    };

    const data = result?.data || [];
    const meta = result;
    const chartData = report.chartDataFn ? report.chartDataFn(data, meta) : null;
    const tableData = report.tableDataFn ? report.tableDataFn(data, meta) : data;
    const chartOptions = report.chartOptions || BASE_CHART_OPTIONS;

    return (
        <div>
            {/* Controls bar */}
            <div className="flex flex-wrap align-items-end gap-3 mb-3 p-3 border-round-lg surface-50 border-1 border-200">
                {report.hasDateRange && (
                    <div>
                        <label className="block text-sm text-600 mb-1">Date Range</label>
                        <Calendar
                            value={dateRange}
                            onChange={(e) => setDateRange(e.value)}
                            selectionMode="range"
                            readOnlyInput
                            showIcon
                            dateFormat="yy-mm-dd"
                            style={{ width: 260 }}
                        />
                    </div>
                )}
                {report.extraControls && report.extraControls(extraParams, setExtraParams)}
                <Button
                    label="Run Report"
                    icon={loading ? "pi pi-spin pi-spinner" : "pi pi-play"}
                    disabled={loading}
                    onClick={runReport}
                    className="p-button-sm"
                />
                {data.length > 0 && (
                    <Button
                        label="Export CSV"
                        icon="pi pi-download"
                        className="p-button-sm p-button-outlined p-button-secondary"
                        onClick={exportCsv}
                    />
                )}
            </div>

            {error && <Message severity="error" text={error} className="w-full mb-3" />}
            {loading && <ProgressBar mode="indeterminate" style={{ height: 4 }} className="mb-3" />}

            {result && !loading && (
                <>
                    {report.summaryFn && report.summaryFn(meta)}

                    {chartData && (
                        <Card className="shadow-1 border-round-lg mb-3">
                            <div style={{ height: 300 }}>
                                <Chart
                                    type={report.chartType || "bar"}
                                    data={chartData}
                                    options={chartOptions}
                                    style={{ height: "100%" }}
                                />
                            </div>
                        </Card>
                    )}

                    <Card className="shadow-1 border-round-lg">
                        <div className="flex justify-content-between align-items-center mb-2">
                            <span className="text-600 text-sm">{tableData.length} records</span>
                        </div>
                        <DataTable
                            value={tableData}
                            paginator
                            rows={15}
                            rowsPerPageOptions={[15, 25, 50, 100]}
                            size="small"
                            stripedRows
                            removableSort
                            emptyMessage="No data. Run the report to see results."
                            scrollable
                            scrollHeight="flex"
                        >
                            {report.columns.map((col) => (
                                <Column
                                    key={col.field}
                                    field={col.field}
                                    header={col.header}
                                    sortable={col.sortable}
                                    body={col.body}
                                />
                            ))}
                        </DataTable>
                    </Card>
                </>
            )}

            {!result && !loading && (
                <div className="flex flex-column align-items-center justify-content-center py-6 text-600">
                    <i className="pi pi-chart-bar text-4xl mb-3 text-300" />
                    <span>Configure filters and click <strong>Run Report</strong> to view data.</span>
                </div>
            )}
        </div>
    );
};

// ─── StandardReports (main) ────────────────────────────────────────────────

const StandardReports = () => {
    const [selectedGroup, setSelectedGroup] = useState(REPORT_GROUPS[0]);
    const [selectedReport, setSelectedReport] = useState(REPORT_GROUPS[0].reports[0]);

    return (
        <div className="flex gap-3" style={{ flex: 1, minHeight: 0 }}>
            <div style={{ width: 220, flexShrink: 0 }}>
                {REPORT_GROUPS.map((group) => (
                    <div key={group.label} className="mb-2">
                        <div className="flex align-items-center gap-2 px-2 py-2 text-xs font-semibold text-500 uppercase" style={{ letterSpacing: "0.08em" }}>
                            <i className={group.icon} style={{ color: group.color }} />
                            {group.label}
                        </div>
                        {group.reports.map((report) => (
                            <div
                                key={report.id}
                                className="px-3 py-2 border-round cursor-pointer text-sm mb-1"
                                style={{
                                    background: selectedReport?.id === report.id ? `${group.color}18` : "transparent",
                                    color: selectedReport?.id === report.id ? group.color : undefined,
                                    fontWeight: selectedReport?.id === report.id ? 600 : undefined,
                                    borderLeft: selectedReport?.id === report.id ? `3px solid ${group.color}` : "3px solid transparent",
                                    transition: "all 0.15s",
                                }}
                                onClick={() => { setSelectedGroup(group); setSelectedReport(report); }}
                            >
                                {report.label}
                            </div>
                        ))}
                    </div>
                ))}
            </div>

            <Divider layout="vertical" className="mx-0" />

            <div className="flex-1 min-w-0">
                {selectedReport && (
                    <>
                        <div className="mb-3">
                            <div className="flex align-items-center gap-2 mb-1">
                                <i className={selectedGroup.icon} style={{ color: selectedGroup.color }} />
                                <h3 className="m-0 font-semibold text-900">{selectedReport.label}</h3>
                            </div>
                            <p className="text-600 text-sm m-0">{selectedReport.description}</p>
                        </div>
                        <ReportViewer key={selectedReport.id} report={selectedReport} />
                    </>
                )}
            </div>
        </div>
    );
};

export default StandardReports;